import React, { useEffect, useState } from "react";
import { Formik, useFormik } from "formik";
import {
  Card,
  CardBody,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Form,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Alert,
  Collapse,
} from "reactstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Dropzone from "react-dropzone";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import ThemeOptionService from "../../../Services/QRCodeServices/QrThemeService/ThemeOptionService";
import QrThemeService from "../../../Services/QRCodeServices/QrThemeService/QrThemeService";
import Select from "react-select";

// Utility function to format file sizes
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const ThemeOptionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [themeData, setThemeData] = useState([]);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [editingKeyIndex, setEditingKeyIndex] = useState(null);
  const [keyFormData, setKeyFormData] = useState({ key: "", description: "" });
  const [openAccordion, setOpenAccordion] = useState(0); // Track which accordion is open

  useEffect(() => {
    const fetchThemeDetails = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await ThemeOptionService.detail(id);
        setData(response);
      } catch (error) {
        console.error("Error fetching theme details:", error);
        toast.error("Failed to fetch theme details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchThemeDetails();
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await QrThemeService.get();
        setThemeData(response);
      } catch (error) {
        console.error("Error fetching theme types:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const formik = useFormik({
    initialValues: {
      title: data?.title || "",
      description: data?.description || "",
      isDefault: data?.isDefault ?? false,
      themeId: data?.themeId || null,
      keys: data?.keys?.length ? data.keys : [],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!values.title) errors.title = "Theme title is required";
      if (!values.description)
        errors.description = "Theme description is required";
      if (!values.themeId) errors.themeId = "Theme selection is required";

      if (values.keys.length === 0) {
        errors.keys = "At least one key is required";
      }

      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      setIsLoading(true);
      try {
        const payload = {
          title: values.title,
          description: values.description,
          isDefault: values.isDefault,
          themeId: values.themeId,
          keys: values.keys,
        };

        if (id) {
          await ThemeOptionService.update(id, payload);
          toast.success("Theme option updated successfully");
        } else {
          await ThemeOptionService.create(payload);
          toast.success("Theme option created successfully");
        }

        navigate("/theme-option");
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error(error.response?.data?.message || "Failed to save theme");
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }
    },
  });

  const handleThemeChange = (selectedOption) => {
    formik.setFieldValue(
      "themeId",
      selectedOption ? selectedOption.value : null
    );
  };

  const handleOpenKeyModal = (index = null) => {
    if (index !== null) {
      // Editing existing key
      setEditingKeyIndex(index);
      setKeyFormData({
        key: formik.values.keys[index].key,
        description: formik.values.keys[index].description,
      });
    } else {
      // Adding new key
      setEditingKeyIndex(null);
      setKeyFormData({ key: "", description: "" });
    }
    setShowKeyModal(true);
  };

  const handleCloseKeyModal = () => {
    setShowKeyModal(false);
    setEditingKeyIndex(null);
    setKeyFormData({ key: "", description: "" });
  };

  const handleSaveKey = () => {
    if (!keyFormData.key.trim() || !keyFormData.description.trim()) {
      toast.error("Both key and description are required");
      return;
    }

    const updatedKeys = [...formik.values.keys];

    if (editingKeyIndex !== null) {
      // Update existing key
      updatedKeys[editingKeyIndex] = { ...keyFormData };
      toast.success("Key updated successfully");
    } else {
      // Add new key
      updatedKeys.push({ ...keyFormData });
      toast.success("Key added successfully");
      setOpenAccordion(updatedKeys.length - 1); // Open newly added accordion
    }

    formik.setFieldValue("keys", updatedKeys);
    handleCloseKeyModal();
  };

  const handleRemoveKey = (index) => {
    if (window.confirm("Are you sure you want to remove this key?")) {
      const updatedKeys = formik.values.keys.filter((_, i) => i !== index);
      formik.setFieldValue("keys", updatedKeys);
      toast.success("Key removed successfully");

      // Adjust open accordion if needed
      if (openAccordion >= updatedKeys.length) {
        setOpenAccordion(Math.max(0, updatedKeys.length - 1));
      }
    }
  };

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? -1 : index);
  };

  const themeOptions = themeData.map((type) => ({
    value: type.id,
    label: type.title,
  }));

  const selectedTheme = themeOptions.find(
    (option) => option.value === formik.values.themeId
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          pageTitle="Themes"
          title={id ? "Edit Theme Option" : "Create Theme Option"}
          breadcrumbItems={[{ title: "Back To List", link: "/theme-option" }]}
        />
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="shadow-sm border-0">
          <CardBody className="p-4">
            <div className="row g-4">
              <div className="col-lg-6">
                <FormGroup>
                  <Label for="theme-title-input" className="fw-semibold">
                    Theme Options Title
                  </Label>
                  <Input
                    type="text"
                    id="theme-title-input"
                    name="title"
                    placeholder="Enter theme title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.title && !!formik.errors.title}
                  />
                  <FormFeedback>{formik.errors.title}</FormFeedback>
                </FormGroup>

                <FormGroup>
                  <Label for="themeId" className="fw-semibold">
                    Theme Option Type
                  </Label>
                  <Select
                    id="themeId"
                    name="themeId"
                    options={themeOptions}
                    value={selectedTheme || null}
                    onChange={handleThemeChange}
                    onBlur={() => formik.setFieldTouched("themeId", true)}
                    placeholder="Select Theme Type"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor:
                          formik.touched.themeId && formik.errors.themeId
                            ? "#dc3545"
                            : base.borderColor,
                        "&:hover": {
                          borderColor:
                            formik.touched.themeId && formik.errors.themeId
                              ? "#dc3545"
                              : base.borderColor,
                        },
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {formik.touched.themeId && formik.errors.themeId && (
                    <div className="text-danger mt-1">
                      {formik.errors.themeId}
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label for="theme-description-input" className="fw-semibold">
                    Theme Option Description
                  </Label>
                  <Input
                    type="textarea"
                    id="theme-description-input"
                    name="description"
                    rows="5"
                    placeholder="Enter theme description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched.description && !!formik.errors.description
                    }
                  />
                  <FormFeedback>{formik.errors.description}</FormFeedback>
                </FormGroup>

                <FormGroup check switch>
                  <Input
                    type="switch"
                    id="isDefaultSwitch"
                    name="isDefault"
                    checked={formik.values.isDefault}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <Label check for="isDefaultSwitch" className="ms-2">
                    Default Theme Option
                  </Label>
                </FormGroup>
              </div>

              <div className="col-lg-6">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fw-semibold">Theme Keys</h5>
                  {!id ? (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => handleOpenKeyModal()}
                    >
                      <i className="ri-add-line me-1"></i> Add Key
                    </Button>
                  ) : null}
                </div>

                {formik.errors.keys &&
                  typeof formik.errors.keys === "string" && (
                    <Alert color="danger" className="py-2">
                      {formik.errors.keys}
                    </Alert>
                  )}

                {formik.values.keys.length === 0 ? (
                  <div className="text-center py-4">
                    <div className="text-muted">
                      <i className="ri-key-2-line fs-1 mb-2 d-block"></i>
                      <p>No keys added yet. Click "Add Key" to get started.</p>
                    </div>
                  </div>
                ) : (
                  <div className="accordion-custom">
                    {formik.values.keys.map((item, index) => (
                      <Card key={index} className="mb-3  shadow-sm">
                        <div
                          className="card-header  border-bottom-0 py-2 cursor-pointer"
                          onClick={() => toggleAccordion(index)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <i className="ri-key-2-line me-2 text-primary"></i>
                              <span className="fw-medium">
                                {item.key || `Key ${index + 1}`}
                              </span>
                              <i
                                className={`ri-arrow-${
                                  openAccordion === index ? "up" : "down"
                                }-s-line ms-2 text-muted`}
                              ></i>
                            </div>
                            {!id ? (
                              <div
                                className="d-flex gap-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  color="outline-secondary"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenKeyModal(index);
                                  }}
                                  title="Edit Key"
                                >
                                  <i className="ri-edit-line"></i>
                                </Button>
                                <Button
                                  color="outline-danger"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveKey(index);
                                  }}
                                  title="Remove Key"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <Collapse isOpen={openAccordion === index}>
                          <CardBody>
                            <div className="row g-3">
                              <div className="col-12">
                                <Label className="fw-semibold text-muted">
                                  Key Name
                                </Label>
                                <div className="p-2 bg-light rounded">
                                  <code className="text-dark">{item.key}</code>
                                </div>
                              </div>
                              <div className="col-12">
                                <Label className="fw-semibold text-muted">
                                  Description
                                </Label>
                                <div className="p-3 bg-light rounded">
                                  <p className="mb-0 text-dark">
                                    {item.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </CardBody>
                        </Collapse>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="text-end mt-4">
              <Button
                type="submit"
                color="success"
                className="px-4"
                disabled={formik.isSubmitting || isLoading}
              >
                {isLoading ? "Saving..." : id ? "Update Theme" : "Create Theme"}
              </Button>
            </div>
          </CardBody>
        </Card>
      </Form>

      {/* Key Modal */}
      <Modal isOpen={showKeyModal} toggle={handleCloseKeyModal} centered>
        <ModalHeader toggle={handleCloseKeyModal}>
          <i className="ri-key-2-line me-2"></i>
          {editingKeyIndex !== null ? "Edit Key" : "Add New Key"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="fw-semibold">
              Key Name <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Enter key name (e.g., primary_color, font_size)"
              value={keyFormData.key}
              onChange={(e) =>
                setKeyFormData({ ...keyFormData, key: e.target.value })
              }
            />
            <div className="form-text">
              Use lowercase letters, numbers, and underscores only
            </div>
          </FormGroup>
          <FormGroup>
            <Label className="fw-semibold">
              Description <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows="4"
              placeholder="Describe what this key is used for..."
              value={keyFormData.description}
              onChange={(e) =>
                setKeyFormData({
                  ...keyFormData,
                  description: e.target.value,
                })
              }
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleCloseKeyModal}>
            Cancel
          </Button>
          <Button color="success" onClick={handleSaveKey}>
            <i className="ri-save-line me-1"></i>
            {editingKeyIndex !== null ? "Update Key" : "Add Key"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ThemeOptionForm;
